/**
 * F-110 inbox state — one store for the whole tab, shared by every bell (the
 * dashboard's and the board screen's) so navigating between them never shows
 * two different unread counts or costs a second fetch.
 *
 * Polling, not push: there is no user-level socket in Kola, only per-board
 * collaboration rooms (docs/04 §1), and a mention on board B has to reach you
 * while you are looking at board A. A 30 s poll over a ~30-person tool is a few
 * bytes a minute; standing up a second socket layer for it would be the wrong
 * trade.
 *
 * The poll pauses while the tab is hidden and fires immediately on the way
 * back, so a laptop asleep for an hour costs one request rather than 120 — and
 * the bell is fresh by the time its owner has finished looking at it.
 */
import type { NotificationItem } from "@kola/shared";
import { createStore } from "zustand/vanilla";

import { fetchNotifications, markNotificationsRead } from "../../core/api.js";

export const NOTIFICATIONS_POLL_MS = 30_000;
/** How many rows the bell holds. The dropdown scrolls; nobody reads past this. */
export const NOTIFICATIONS_PAGE = 50;

export interface NotificationsState {
  items: NotificationItem[];
  unread: number;
  /** True only for the FIRST load — later polls refresh in place, no spinner. */
  loading: boolean;
  /** Set when the last poll failed; cleared by the next success. */
  error: boolean;
  refresh(): Promise<void>;
  markRead(ids?: readonly string[]): Promise<void>;
}

export const notificationsStore = createStore<NotificationsState>((set, get) => ({
  items: [],
  unread: 0,
  loading: true,
  error: false,

  async refresh() {
    try {
      const response = await fetchNotifications(NOTIFICATIONS_PAGE);
      set({ items: response.items, unread: response.unread, loading: false, error: false });
    } catch {
      // Keep whatever is on screen: a dropped poll (offline, a deploy
      // handover) must not blank an inbox the user is reading.
      set({ loading: false, error: true });
    }
  },

  async markRead(ids) {
    const readAt = new Date().toISOString();
    // Optimistic: the badge must clear the instant it is clicked. A failed
    // request leaves the server's truth in place and the next poll restores it.
    set({
      items: get().items.map((item) =>
        item.readAt === null && (ids === undefined || ids.includes(item.id))
          ? { ...item, readAt }
          : item,
      ),
      unread:
        ids === undefined
          ? 0
          : get().items.filter((i) => i.readAt === null && !ids.includes(i.id)).length,
    });
    try {
      const { unread } = await markNotificationsRead(ids);
      set({ unread });
    } catch {
      void get().refresh();
    }
  },
}));

let started = 0;
let timer: ReturnType<typeof setInterval> | null = null;

/**
 * A scheduled tick. Skipped while the tab is hidden — that is the whole point
 * of the gate, and `onVisibility` catches up the moment it comes back.
 */
function poll(): void {
  if (document.visibilityState === "hidden") return;
  void notificationsStore.getState().refresh();
}

function onVisibility(): void {
  if (document.visibilityState === "visible") poll();
}

/**
 * Start polling (ref-counted, so N mounted bells share one interval). Returns
 * the stop function — mount-scoped, like an effect cleanup.
 */
export function startNotificationPolling(intervalMs = NOTIFICATIONS_POLL_MS): () => void {
  started += 1;
  if (started === 1) {
    // The FIRST fetch ignores the visibility gate. A tab opened in the
    // background (⌘-click a board link, a session restored on launch) is
    // hidden at mount, and gating this one would leave the bell stuck on
    // "Loading…" with no badge until something happened to focus the tab.
    // It is one request; the gate exists to stop the other 119 an hour.
    void notificationsStore.getState().refresh();
    timer = setInterval(poll, intervalMs);
    document.addEventListener("visibilitychange", onVisibility);
  }
  return () => {
    started -= 1;
    if (started > 0) return;
    if (timer !== null) clearInterval(timer);
    timer = null;
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
